import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import {
  FilePlus,
  Files,
  Users,
  Buildings,
  GearSix,
  House,
  SignOut,
  CaretLeft,
  CaretRight,
  MapTrifold,
  Bed,
  Compass,
  Stack,
  Seal,
  ArrowsClockwise,
  ChartBar,
} from "@phosphor-icons/react";

const navItems = [
  { icon: House, label: "Dashboard", path: "/dashboard" },
  { icon: Files, label: "Proposals", path: "/proposals" },
  { icon: ArrowsClockwise, label: "Reservations", path: "/reservations" },
  { icon: Seal, label: "Confirmations", path: "/confirmations" },
  { icon: ChartBar, label: "Revenue", path: "/revenue" },
  { icon: Users, label: "Clients", path: "/clients" },
  { icon: Buildings, label: "Hotel Library", path: "/hotels" },
  { icon: MapTrifold, label: "Tour Library", path: "/tours" },
  { icon: GearSix, label: "Settings", path: "/settings" },
];

const proposalTypes = [
  { icon: Bed, label: "Hotel Proposal", type: "hotels" },
  { icon: Compass, label: "Sightseeing Proposal", type: "sightseeing" },
  { icon: Stack, label: "Combined Proposal", type: "combined" },
];

const Sidebar = ({ collapsed, onToggle, onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [showNewMenu, setShowNewMenu] = useState(false);

  const goTo = (path) => {
    navigate(path);
    onNavigate?.();
  };

  const isActive = (path) => {
    if (path === "/proposals/new" && location.pathname.includes("/edit")) {
      return true;
    }
    return location.pathname === path || 
           (path === "/proposals" && location.pathname === "/proposals");
  };

  const isNewProposalActive = location.pathname.includes("/proposals/new") || location.pathname.includes("/edit");

  return (
    <aside
      className={`bg-navy flex flex-col h-screen sticky top-0 sidebar-transition ${
        collapsed ? "w-[72px]" : "w-[240px]"
      }`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="p-5 border-b border-gold/20">
        {!collapsed ? (
          <>
            <h1 className="font-serif text-lg text-white tracking-wide">ProposalDesk</h1>
            <p className="text-[10px] text-gold uppercase tracking-[0.15em] mt-1">
              Travel Proposals
            </p>
            <p className="text-[10px] text-white/30 mt-1.5">Muskan Travel Associates</p>
          </>
        ) : (
          <div className="text-center">
            <span className="font-serif text-xl text-white">P</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {/* New Proposal with sub-menu */}
        <div className="relative">
          <button
            onClick={() => collapsed ? goTo("/proposals/new?type=combined") : setShowNewMenu((v) => !v)}
            className={`w-full flex items-center gap-3 px-5 py-3 text-sm transition-colors border-l-2 ${
              isNewProposalActive
                ? "text-white bg-gold/10 border-l-gold"
                : "text-white/60 hover:text-white hover:bg-white/5 border-l-transparent"
            }`}
            data-testid="nav-new-proposal"
          >
            <FilePlus size={20} weight={isNewProposalActive ? "fill" : "regular"} />
            {!collapsed && <span>New Proposal</span>}
            {!collapsed && (
              <CaretRight size={12} className={`ml-auto transition-transform ${showNewMenu ? "rotate-90" : ""}`} />
            )}
          </button>
          {showNewMenu && !collapsed && (
            <div className="bg-navy/80 border-l-2 border-l-transparent">
              {proposalTypes.map((pt) => (
                <button
                  key={pt.type}
                  onClick={() => {
                    goTo(`/proposals/new?type=${pt.type}`);
                    setShowNewMenu(false);
                  }}
                  className="w-full flex items-center gap-2.5 pl-11 pr-5 py-2 text-xs text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                  data-testid={`new-${pt.type}`}
                >
                  <pt.icon size={15} />
                  <span>{pt.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {navItems.map((item) => (
          <button
            key={item.path}
            onClick={() => goTo(item.path)}
            className={`w-full flex items-center gap-3 px-5 py-3 text-sm transition-colors border-l-2 ${
              isActive(item.path)
                ? "text-white bg-gold/10 border-l-gold"
                : "text-white/60 hover:text-white hover:bg-white/5 border-l-transparent"
            }`}
            data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
          >
            <item.icon size={20} weight={isActive(item.path) ? "fill" : "regular"} />
            {!collapsed && <span>{item.label}</span>}
          </button>
        ))}
      </nav>

      {/* User info & Logout */}
      <div className="border-t border-white/10 p-4">
        {!collapsed && user && (
          <p className="text-xs text-white/40 mb-3 truncate">{user.email}</p>
        )}
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-2 py-2 text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          data-testid="logout-btn"
        >
          <SignOut size={20} />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 w-6 h-6 bg-white border border-border flex items-center justify-center text-navy hover:bg-cream transition-colors"
        data-testid="sidebar-toggle"
      >
        {collapsed ? <CaretRight size={14} /> : <CaretLeft size={14} />}
      </button>
    </aside>
  );
};

export default Sidebar;
